"use client";

import { ReactNode, useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { Rail } from "./Rail";
import { Panel } from "./Panel";
import { MoreSheet } from "./MoreSheet";
import { BottomNav } from "./BottomNav";
import { detectEventId, detectZone } from "./nav-model";
import { ToastProvider } from "../ui/Toast";
import { PermissionProvider } from "@/contexts/PermissionContext";
import {
  useEventDetailEvent,
  useEventDetailStore,
} from "@/stores/eventDetailStore";
import { cn } from "@/lib/utils";
import { Locale } from "@/i18n-config";
import type { User } from "@/types/auth";
import type { Dictionary } from "@/types/dictionary";

interface DashboardLayoutProps {
  children: ReactNode;
  user?: User;
  dict: Dictionary;
  lang: Locale;
}

function DashboardLayoutContent({
  children,
  user,
  dict,
  lang,
}: DashboardLayoutProps) {
  const pathname = usePathname();
  const [sheetOpen, setSheetOpen] = useState(false);

  const eventId = detectEventId(pathname, lang);
  const zone = detectZone(pathname, lang);
  // The mark only appears on the home screen; deeper pages lead with their own title.
  const showLogo = pathname.replace(`/${lang}`, "") === "/dashboard";
  const event = useEventDetailEvent();
  const fetchEvent = useEventDetailStore((s) => s.fetchEvent);

  // Ensure the event-context panel and EventCrumb can show the real event name
  // on every sub-route, not just pages that fetch it themselves.
  useEffect(() => {
    if (eventId && event?.id !== eventId) fetchEvent(eventId);
  }, [eventId, event?.id, fetchEvent]);

  // A panel has to earn its column: it needs a context block or enough sub-pages
  // to be worth 256px. Ekip has neither (two links, no identity) — it uses tabs.
  const panelKind =
    zone === "event" ? "event" : zone === "admin" ? "admin" : null;

  const contentMargin = panelKind === null ? "lg:ml-[88px]" : "lg:ml-[344px]";

  return (
    <div className="min-h-screen bg-background bg-[radial-gradient(85%_38%_at_6%_0%,rgba(0,214,143,0.13),transparent_62%)] bg-[length:100%_460px] bg-no-repeat lg:bg-none">
      <a href="#main" className="skip-link focus-ring">
        {dict?.layout?.skipToContent ?? "Ana içeriğe atla"}
      </a>

      {/* Desktop: rail + conditional panel (fixed) */}
      <div className="hidden lg:flex fixed inset-y-0 left-0 z-40">
        <Rail user={user} lang={lang} dict={dict} pathname={pathname} />
        {panelKind && (
          <Panel
            kind={panelKind}
            lang={lang}
            dict={dict}
            pathname={pathname}
            eventId={eventId}
          />
        )}
      </div>

      {/* Content */}
      <div
        className={cn("transition-[margin-left] duration-200", contentMargin)}
      >
        <main
          id="main"
          className={cn(
            // overflow-x-clip: tek bir taşan çocuk body'nin scrollWidth'ini
            // büyütüyor, sayfa yatay kaydırılabiliyor ve viewport'a sabitlenmiş
            // BottomNav içerikle hizasını kaybediyordu. clip, hidden'ın aksine
            // scroll container yaratmaz — sticky başlıklar çalışmaya devam eder.
            "relative overflow-x-clip p-4 pb-28 scroll-mt-20 lg:p-8 lg:pb-8 lg:pt-8",
            // Clear the floating logo so page titles start below it, not beside it.
            showLogo ? "pt-16" : "pt-6",
          )}
          tabIndex={-1}
        >
          {showLogo && (
            <Link
              href={`/${lang}/dashboard`}
              className="absolute left-4 top-5 z-10 lg:hidden"
            >
              <Image
                src="/venuer-logo.png"
                alt="Venuer"
                width={84}
                height={20}
                className="h-5 w-auto"
                priority
              />
            </Link>
          )}
          <div className="max-w-[1440px] mx-auto">{children}</div>
        </main>
      </div>

      {/* Mobile bottom nav */}
      <BottomNav lang={lang} dict={dict} onMore={() => setSheetOpen(true)} />

      {/* Mobile More sheet */}
      {sheetOpen && (
        <MoreSheet
          mode={eventId ? "event" : "global"}
          lang={lang}
          dict={dict}
          eventId={eventId}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </div>
  );
}

function DashboardLayout(props: DashboardLayoutProps) {
  return (
    <ToastProvider>
      <PermissionProvider user={props.user || null}>
        <DashboardLayoutContent {...props} />
      </PermissionProvider>
    </ToastProvider>
  );
}

export { DashboardLayout, type DashboardLayoutProps };
